// Pure adapters from the GET /api/gbp/:siteId wire shapes (GbpLocationData /
// GbpReviewsPage — apps/dashboard/web/src/lib/types.ts, close mirrors of
// Google's response shape) to the dashboard component view-models
// (GbpLocationView / GbpReviewView — @pageboss/shared). Relocated out of
// routes/site-gbp.tsx (Task 15) so the route file stays focused on data
// fetching + state routing. Behavior unchanged from the pre-Task-15 version.
import type { GbpLocationView, GbpReviewView } from '@pageboss/shared';
import type { GbpAddress, GbpLocationData, GbpReviewsPage } from '@/lib/types';

export function toLocationView(
  data: GbpLocationData,
  reviews: GbpReviewsPage | undefined,
): GbpLocationView {
  return {
    id: data.resourceName,
    name: data.title,
    category: data.primaryCategory,
    placeId: data.placeId,
    address: toAddressView(data.address),
    serviceArea: data.serviceArea,
    phone: data.phone,
    websiteUrl: data.websiteUrl,
    hours: data.hours,
    latLng: data.latLng,
    // exactOptionalPropertyTypes: only set these keys when there is a real
    // number — absent (not `undefined`-valued) at zero reviews.
    ...(reviews?.averageRating != null ? { rating: reviews.averageRating } : {}),
    ...(reviews?.totalReviewCount != null ? { reviewCount: reviews.totalReviewCount } : {}),
  };
}

export function toAddressView(address: GbpAddress | null): GbpLocationView['address'] {
  if (!address) return null;
  return {
    lines: address.lines,
    city: address.city,
    region: address.region,
    postalCode: address.postalCode,
  };
}

export function toReviewView(review: GbpReviewsPage['reviews'][number]): GbpReviewView {
  // The D1 overlay (review.reply) is fresher than Google's ownerReply for
  // anything done through pageboss: a 'posted' overlay shows as the owner
  // reply immediately (Google's copy — with its timestamp — arrives next
  // sync); a 'drafted' overlay becomes the editable draft.
  const posted =
    review.reply?.status === 'posted'
      ? { text: review.reply.text }
      : review.ownerReply ?? undefined;
  return {
    id: review.googleReviewId,
    reviewerName: review.reviewerName,
    rating: review.rating,
    text: review.comment,
    postedAt: review.postedAt,
    // exactOptionalPropertyTypes: omit the key entirely rather than set it
    // to `undefined` when there is no value.
    ...(posted ? { ownerReply: posted } : {}),
    ...(review.reply?.status === 'drafted' ? { replyDraft: review.reply.text } : {}),
  };
}
